<template>
    <LayoutPage v-if="cookie.uuid">
        <template #header>
            <CookieProjectHeader :actions="actions" @publish="handlePublish" />
        </template>

        <template #sidebar>
            <router-view />
        </template>

        <template #preview>
            <CookieProjectPreview />
        </template>

        <template #footer>
            <CookieProjectFooter :actions="actions" @publish="handlePublish" />
        </template>
    </LayoutPage>
    <!-- Onboarding  -->
    <CookieCreator v-if="isCreatorShown" />
    <!-- First steps Video -->
    <cHelpVideosAfterOnboarding v-else />
    <!-- Modal for renaming -->
    <ModalInput
        v-model:open="showRenameModal"
        v-model:text="projectName"
        :modal-data="modalRenameData"
        @cta="handleRenameProject(projectName)"
        @cancel="showRenameModal = false"
    />
    <!-- Modal for deleting -->
    <ModalConfirm
        v-model="showDeleteModal"
        :modal-data="modalDeleteData"
        @cta="deleteProject"
        @cancel="showDeleteModal = false"
    />
</template>

<script>
import { markRaw } from "vue";
import { LayoutPage } from "@easy/ui/layouts";
import CookieProjectHeader from "@/apps/cookie/project/CookieProjectHeader.vue";
import CookieProjectPreview from "@/apps/cookie/project/CookieProjectPreview.vue";
import CookieProjectFooter from "@/apps/cookie/project/CookieProjectFooter.vue";
import CookieCreator from "@/apps/cookie/project/onboarding/CookieCreator.vue";
import { makeLogger } from "@/shared/consola";
import { mapActions, mapState, mapGetters, mapMutations } from "vuex";
import { PencilLineIcon, TrashBinIcon, CodeIcon, InvisibleIcon } from "@easy/ui/icons";
import { ModalInput, ModalConfirm } from "@easy/ui/modals";
import { EventBus } from "@/shared/projects/event-bus";
import { COOKIE_PREVIEW_KIND } from "@/apps/cookie/cookie.routes";
import { storeToRefs } from "pinia";
import { useCookieCreatorStore } from "@/apps/cookie/project/onboarding/cookie-creator.store";
import cHelpVideosAfterOnboarding from "@/shared/components/help/cHelpVideosAfterOnboarding.vue";

const logger = makeLogger("CookieProjectPage");

export default {
    components: {
        cHelpVideosAfterOnboarding,
        LayoutPage,
        CookieProjectHeader,
        CookieProjectPreview,
        CookieProjectFooter,
        ModalInput,
        ModalConfirm,
        CookieCreator,
    },
    mounted() {
        this.$watch(() => this.$route.params.projectUuid, this.handleProjectChange, {
            immediate: true,
        });
        this.$watch(
            () => this.cookie.name,
            (cookieName) => {
                this.projectName = cookieName || "";
                document.title = `Easycookie | ${cookieName}`;
                if (this.projectName) {
                    this.autoOpenCreator(this.cookie);
                }
            },
            { immediate: true }
        );
        // FIXME: walk around for now when (publish)
        EventBus.$on("askForChanges", (resolve) => {
            resolve(false);
        });
    },
    watch: {
        iframeLoaded(value) {
            if (value && this.waitForIframeAndPublish) {
                this.publishProject2();
                this.waitForIframeAndPublish = false;
            }
        },
    },
    setup() {
        const cookieCreatorStore = useCookieCreatorStore();
        const { isCreatorShown } = storeToRefs(cookieCreatorStore);
        const { autoOpenCreator } = cookieCreatorStore;
        return { isCreatorShown, autoOpenCreator };
    },
    data() {
        return {
            showRenameModal: false,
            showDeleteModal: false,
            projectName: "",
            waitForIframeAndPublish: false,
        };
    },
    computed: {
        ...mapState(["globalUrl"]),
        ...mapState("cookie", ["cookie", "iframeLoaded"]),
        ...mapGetters(["isPublished", "appUrl"]),
        actions() {
            return [
                {
                    label: this.$t("shared.copy_embed"),
                    icon: markRaw(CodeIcon),
                    handler: () => {
                        this.showCodeModal();
                    },
                    mobileOnly: true,
                },
                {
                    label: this.$t("shared.rename"),
                    icon: markRaw(PencilLineIcon),
                    handler: () => {
                        this.showRenameModal = true;
                    },
                },
                this.isPublished(this.cookie)
                    ? {
                          label: this.$t("shared.unpublish"),
                          icon: markRaw(InvisibleIcon),
                          handler: () => {
                              this.unpublishCookie();
                              logger.log("unpublish");
                          },
                      }
                    : {},
                { isDivider: true },
                {
                    label: this.$t("shared.delete"),
                    isDanger: true,
                    icon: markRaw(TrashBinIcon),
                    handler: () => {
                        this.showDeleteModal = true;
                    },
                },
            ];
        },
        modalRenameData() {
            return {
                title: this.$t("shared.rename"),
                inputLabel: this.$t("shared.project_name"),
                ctaLabel: this.$t("shared.save"),
                cancelLabel: this.$t("shared.cancel"),
            };
        },
        modalDeleteData() {
            return {
                title: this.$t("shared.delete_project"),
                description: this.$t("shared.delete_project_info"),
                ctaLabel: this.$t("shared.delete"),
                cancelLabel: this.$t("shared.cancel"),
            };
        },
    },
    methods: {
        ...mapActions("cookie", [
            "publishProject2",
            "unpublishCookie",
            "loadProject",
            "showCodeModal",
        ]),
        ...mapActions("project", ["removeProject", "renameProject"]),
        ...mapActions("modals", ["showConfirmModal"]),
        ...mapMutations("cookie", ["setIframeLoaded"]),
        handleProjectChange(uuid, oldUuid) {
            if (uuid && uuid !== oldUuid) {
                this.loadProject(uuid);
            }
        },
        async handleRenameProject(newName) {
            // FIXME There is problem with a SNAPSHOT.
            // When you rename project snapshot updates and overrides current data :(
            const error = await this.renameProject({
                tool: "cookie",
                project: this.cookie,
                newName,
            });
            if (!error) {
                this.showRenameModal = false;
            }
        },
        async deleteProject() {
            const error = await this.removeProject({ tool: "cookie", uuid: this.cookie.uuid });
            if (!error) {
                this.showDeleteModal = false;
            }
        },
        async handlePublish() {
            const { name, query } = this.$route;
            const { view } = query;
            if (view !== COOKIE_PREVIEW_KIND.cookie) {
                this.setIframeLoaded(false);
                this.routeTo(name, COOKIE_PREVIEW_KIND.cookie);
            }
            if (this.iframeLoaded) {
                this.publishProject2();
            } else {
                this.waitForIframeAndPublish = true;
            }
        },
        async routeTo(name, forcedView) {
            const { query } = this.$route;
            this.$router.push({
                name,
                query: forcedView ? { ...query, view: forcedView } : query,
            });
        },
    },
};
</script>
@/shared/js/bus
